/**
 * IGA Maturity Assessment — Public Wizard (passos derivados da definition: 10 domínios no IGA = 13 passos).
 *
 * Steps:
 *   0           Intro / Welcome
 *   1-10        Domain questions (10 Likert 0-5 per domain)
 *   11          Legacy systems & project context
 *   12          Lead capture (CNPJ lookup + contact)
 *   final       Auto-submit → redirect to /assessment/iga/resultado/:id
 */
import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import AssessmentConsentStep, { ConsentValues } from '../components/AssessmentConsentStep'
import { GlossaryProvider, useGlossary } from '../components/assessment/glossary'

// ─── Types from backend definition ────────────────────────────────────
interface ScaleItem { value: number; label: string; description: string; color: string }
interface DomainDef { id: number; slug: string; name: string; color: string; tip: string; questions: string[] }
interface LegacyFieldDef {
  id: string; type: 'multi_select' | 'single_select' | 'boolean' | 'number' | 'tags'
  label: string; options?: string[]; placeholder?: string; depends_on?: string; suggestions?: string[]
}
interface LegacySectionDef { id: string; title: string; subtitle: string; fields: LegacyFieldDef[] }
interface MaturityBandDef { id: string; min: number; max: number; label: string; color: string; description: string; tone: string }
interface AssessmentDefinition {
  version: string; pillar: string; slug: string; name: string
  headline?: string; description?: string; estimated_minutes: number
  scale: ScaleItem[]; domains: DomainDef[]; legacy_sections: LegacySectionDef[]
  maturity_bands: MaturityBandDef[]
  max_score_per_question: number; questions_per_domain: number; total_domains: number; max_total_score: number
}

/**
 * O wizard é dirigido pela definition do backend, então serve a qualquer pilar.
 * Os defaults mantêm o IGA idêntico ao que já estava publicado.
 */
export type WizardProps = {
  slug?: string
  storageKey?: string
  resultPath?: string
  eyebrow?: string
  title?: string
}

const SLUG_PADRAO = 'iga-maturity'
const STORAGE_KEY_PADRAO = 'nai_iga_assessment_draft_v1'
const RESULT_PATH_PADRAO = '/assessment/iga/resultado'
const EYEBROW_PADRAO = 'Nai Compliance Center · IGA'
const TITLE_PADRAO = 'IGA Maturity Assessment'

// ─── Helper components ───────────────────────────────────────────────
function ScaleSelector({ scale, value, onChange, color }: {
  scale: ScaleItem[]; value: number | null; onChange: (v: number) => void; color: string
}) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3 mt-3">
      {scale.map((s) => {
        const selected = value === s.value
        return (
          <button
            key={s.value}
            type="button"
            onClick={() => onChange(s.value)}
            className={`group relative flex flex-col items-center justify-center px-1 py-3 rounded-lg border transition-all duration-200 ${
              selected
                ? 'shadow-lg scale-105 border-white'
                : 'border-slate-700 hover:border-slate-500 hover:scale-[1.02]'
            }`}
            style={{
              background: selected ? s.color : 'rgba(15,27,45,0.6)',
              minHeight: 64,
              color: selected ? '#fff' : '#cbd5e1',
            }}
            aria-label={`${s.value} - ${s.label}`}
          >
            <span className="text-lg font-black leading-none">{s.value}</span>
            <span className="text-[10px] font-bold uppercase tracking-wide mt-1 leading-tight text-center">
              {s.label}
            </span>
            <span className="hidden sm:block absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-slate-900 text-slate-200 text-[11px] rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-10 max-w-[220px] whitespace-normal text-center">
              {s.description}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function ProgressBar({ current, total }: { current: number; total: number }) {
  const pct = Math.round((current / total) * 100)
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">
        <span>Passo {current} de {total}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

// ─── Main wizard ─────────────────────────────────────────────────────
export default function AssessmentIGAPage({
  slug = SLUG_PADRAO,
  storageKey = STORAGE_KEY_PADRAO,
  resultPath = RESULT_PATH_PADRAO,
  eyebrow = EYEBROW_PADRAO,
  title = TITLE_PADRAO,
}: WizardProps = {}) {
  const SLUG = slug
  const STORAGE_KEY = storageKey
  const navigate = useNavigate()
  const [defn, setDefn] = useState<AssessmentDefinition | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Wizard state
  const [step, setStep] = useState(0)
  // LGPD — consentimento exibido ANTES da step 0 (intro técnica).
  // Enquanto false, mostramos apenas a tela de consentimento.
  const [consentAccepted, setConsentAccepted] = useState(false)
  const [consents, setConsents] = useState<ConsentValues>({
    relatorio: false, contato: false, newsletter: false,
  })
  const [answers, setAnswers] = useState<Record<string, Record<string, number>>>({})
  const [legacy, setLegacy] = useState<Record<string, any>>({})
  const [contact, setContact] = useState({ full_name: '', email: '', phone: '', job_title: '' })
  const [company, setCompany] = useState({ name: '', cnpj: '', size: '', sector: '' })
  const [cnpjLoading, setCnpjLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // ─── Load definition ───
  useEffect(() => {
    fetch(`/api/v1/assessments/${SLUG}/definition`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((d: AssessmentDefinition) => setDefn(d))
      .catch(e => setLoadError(String(e?.message || e)))
  }, [])

  // ─── Hydrate draft from localStorage ───
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const draft = JSON.parse(raw)
        if (draft.answers) setAnswers(draft.answers)
        if (draft.legacy) setLegacy(draft.legacy)
        if (draft.contact) setContact(draft.contact)
        if (draft.company) setCompany(draft.company)
        if (typeof draft.step === 'number' && draft.step > 0) setStep(draft.step)
        if (draft.consents && draft.consents.relatorio === true) {
          setConsents(draft.consents)
          setConsentAccepted(true)
        }
      }
    } catch {}
  }, [])

  // ─── Persist draft ───
  useEffect(() => {
    if (step === 0 && !consentAccepted) return
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ step, answers, legacy, contact, company, consents })
      )
    } catch {}
  }, [step, answers, legacy, contact, company, consents, consentAccepted])

  /** Aceita o consentimento LGPD e libera o wizard a partir da step 0 (intro técnica). */
  function handleConsentAccept(values: ConsentValues) {
    setConsents(values)
    setConsentAccepted(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Passos derivados da definition: o IGA tem 10 domínios, a Cadeia de Valor tem 7.
  // 0=intro, 1..N=domínios, N+1=contexto (legacy), N+2=contato
  const domainCount = defn?.domains?.length || 10
  const LEGACY_STEP = domainCount + 1
  const CONTACT_STEP = domainCount + 2
  const totalSteps = domainCount + 3
  const isIGA = SLUG === SLUG_PADRAO

  // Rascunho salvo quando o wizard tinha 13 passos fixos pode apontar para um passo
  // que não existe nesta definition — reposiciona no contexto.
  useEffect(() => {
    if (defn && step > CONTACT_STEP) setStep(LEGACY_STEP)
  }, [defn, step, CONTACT_STEP, LEGACY_STEP])
  const currentDomain: DomainDef | null = useMemo(() => {
    if (!defn || step < 1 || step > domainCount) return null
    return defn.domains[step - 1] || null
  }, [defn, step])

  // ─── Validation per step ───
  // Returns { errors, firstFieldId }. Empty errors means the step can advance.
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  function validateCurrentStep(): { errors: Record<string, string>; firstFieldId: string | null } {
    const errors: Record<string, string> = {}
    let firstFieldId: string | null = null

    if (step === 0) return { errors, firstFieldId }

    if (step >= 1 && step <= domainCount && currentDomain) {
      const domAns = answers[String(currentDomain.id)] || {}
      currentDomain.questions.forEach((_, i) => {
        if (typeof domAns[String(i)] !== 'number') {
          const fid = `q-${currentDomain.id}-${i}`
          errors[fid] = 'Responda esta questão'
          if (!firstFieldId) firstFieldId = fid
        }
      })
    } else if (step === LEGACY_STEP && isIGA) {
      if (!Array.isArray(legacy.directory_type) || legacy.directory_type.length === 0) {
        errors['directory_type'] = 'Selecione ao menos um diretório/base autoritativa'
        firstFieldId = firstFieldId || 'directory_type'
      }
      if (!legacy.total_identities_count) {
        errors['total_identities_count'] = 'Informe a faixa de identidades'
        firstFieldId = firstFieldId || 'total_identities_count'
      }
    } else if (step === CONTACT_STEP) {
      if (company.name.trim().length < 2) {
        errors['company.name'] = 'Informe a razão social'
        firstFieldId = firstFieldId || 'company.name'
      }
      if (contact.full_name.trim().length < 2) {
        errors['contact.full_name'] = 'Informe seu nome completo'
        firstFieldId = firstFieldId || 'contact.full_name'
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
        errors['contact.email'] = 'Informe um e-mail válido'
        firstFieldId = firstFieldId || 'contact.email'
      }
      if (contact.phone.trim().length < 8) {
        errors['contact.phone'] = 'Informe seu telefone/WhatsApp'
        firstFieldId = firstFieldId || 'contact.phone'
      }
    }

    return { errors, firstFieldId }
  }

  function focusField(fieldId: string) {
    setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[data-field-id="${fieldId}"]`)
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const input = el.querySelector<HTMLElement>('input, select, textarea, button')
      if (input) input.focus({ preventScroll: true })
    }, 50)
  }

  function handleNext() {
    const { errors, firstFieldId } = validateCurrentStep()
    setValidationErrors(errors)
    if (Object.keys(errors).length > 0) {
      if (firstFieldId) focusField(firstFieldId)
      return
    }
    setValidationErrors({})
    setStep(s => s + 1)
  }

  function canSubmit(): boolean {
    return (
      company.name.trim().length >= 2 &&
      contact.full_name.trim().length >= 2 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email) &&
      contact.phone.trim().length >= 8
    )
  }

  // ─── CNPJ lookup ───
  async function handleCnpjLookup() {
    const raw = company.cnpj.replace(/\D/g, '')
    if (raw.length !== 14) {
      setSubmitError('CNPJ deve ter 14 dígitos')
      return
    }
    setCnpjLoading(true)
    setSubmitError(null)
    try {
      const r = await fetch('/api/v1/assessments/lookup-cnpj', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cnpj: raw }),
      })
      if (!r.ok) throw new Error('CNPJ não localizado')
      const data = await r.json()
      setCompany(prev => ({
        ...prev,
        cnpj: data.cnpj || prev.cnpj,
        name: prev.name || data.razao_social || data.nome_fantasia || '',
        sector: prev.sector || '',
      }))
    } catch (e: any) {
      setSubmitError(e?.message || 'Falha na consulta de CNPJ')
    } finally {
      setCnpjLoading(false)
    }
  }

  // ─── Submit ───
  async function handleSubmit() {
    if (submitting) return
    setSubmitting(true); setSubmitError(null)
    try {
      const r = await fetch(`/api/v1/assessments/${SLUG}/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact, company, answers, legacy,
          consents,
          referrer: document.referrer || undefined,
        }),
      })
      if (!r.ok) {
        const txt = await r.text()
        throw new Error(`HTTP ${r.status}: ${txt.slice(0, 200)}`)
      }
      const data = await r.json()
      localStorage.removeItem(STORAGE_KEY)
      navigate(`${resultPath}/${data.submission_id}`)
    } catch (e: any) {
      setSubmitError(e?.message || 'Falha ao enviar. Tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Render ───
  if (loadError) {
    return <CenteredMessage title="Erro ao carregar" detail={loadError} />
  }
  if (!defn) {
    return <CenteredMessage title="Carregando assessment…" detail="" />
  }

  return (
    <GlossaryProvider>
    <div className="min-h-screen bg-gradient-to-b from-[#050912] via-[#0a131f] to-[#050912] text-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#00284a]/85 border-b border-slate-800 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <img src="https://nai-it.com.br/wp-content/uploads/2025/11/logo_nai_branco.png"
               alt="NAI" className="h-9 w-auto" />
          <div className="border-l border-slate-700 pl-3 flex-1">
            <div className="text-xs font-bold uppercase tracking-widest text-cyan-400">
              {eyebrow}
            </div>
            <div className="text-sm font-semibold text-white">
              {title}
            </div>
          </div>
          <div className="hidden sm:block text-xs text-slate-400">
            ~{defn.estimated_minutes} min
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* LGPD — tela de consentimento exibida ANTES da intro técnica (step 0). */}
        {!consentAccepted ? (
          <AssessmentConsentStep
            partner="NAI"
            initial={consents}
            onAccept={handleConsentAccept}
          />
        ) : (
        <>
        {step > 0 && (
          <div className="mb-8">
            <ProgressBar current={step} total={totalSteps - 1} />
          </div>
        )}

        {Object.keys(validationErrors).length > 0 && (
          <div className="mb-6 p-4 rounded-xl bg-red-900/30 border border-red-700/60 text-red-200 text-sm flex items-start gap-3 animate-fadeIn">
            <span className="text-xl leading-none">⚠️</span>
            <div>
              <div className="font-bold text-red-100 mb-0.5">
                {Object.keys(validationErrors).length === 1
                  ? 'Falta uma resposta'
                  : `Faltam ${Object.keys(validationErrors).length} respostas`}
              </div>
              <div className="text-red-300/90 text-xs">
                Os campos destacados em vermelho precisam ser preenchidos antes de continuar.
              </div>
            </div>
          </div>
        )}

        {step === 0 && <StepIntro defn={defn} onStart={() => setStep(1)} />}

        {step >= 1 && step <= domainCount && currentDomain && (
          <StepDomain
            domain={currentDomain}
            scale={defn.scale}
            answers={answers[String(currentDomain.id)] || {}}
            errors={validationErrors}
            onAnswer={(qi, v) => {
              setAnswers(prev => ({
                ...prev,
                [String(currentDomain.id)]: { ...(prev[String(currentDomain.id)] || {}), [String(qi)]: v },
              }))
              const fid = `q-${currentDomain.id}-${qi}`
              if (validationErrors[fid]) {
                setValidationErrors(prev => { const { [fid]: _drop, ...rest } = prev; return rest })
              }
            }}
          />
        )}

        {step === LEGACY_STEP && (
          <StepLegacy
            stepNumber={LEGACY_STEP}
            isIGA={isIGA}
            sections={defn.legacy_sections}
            values={legacy}
            errors={validationErrors}
            onChange={(field, value) => {
              setLegacy(prev => ({ ...prev, [field]: value }))
              if (validationErrors[field]) {
                setValidationErrors(prev => { const { [field]: _drop, ...rest } = prev; return rest })
              }
            }}
          />
        )}

        {step === CONTACT_STEP && (
          <StepContact
            contact={contact}
            company={company}
            errors={validationErrors}
            onContactChange={(k, v) => {
              setContact(prev => ({ ...prev, [k]: v }))
              const fid = `contact.${k}`
              if (validationErrors[fid]) {
                setValidationErrors(prev => { const { [fid]: _drop, ...rest } = prev; return rest })
              }
            }}
            onCompanyChange={(k, v) => {
              setCompany(prev => ({ ...prev, [k]: v }))
              const fid = `company.${k}`
              if (validationErrors[fid]) {
                setValidationErrors(prev => { const { [fid]: _drop, ...rest } = prev; return rest })
              }
            }}
            onCnpjLookup={handleCnpjLookup}
            cnpjLoading={cnpjLoading}
            error={submitError}
          />
        )}

        {/* Navigation */}
        {step > 0 && (
          <nav className="mt-10 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => { setValidationErrors({}); setStep(s => Math.max(0, s - 1)) }}
              className="px-5 py-2.5 rounded-full border border-slate-700 text-slate-300 hover:bg-slate-800/60 hover:border-slate-500 transition text-sm font-semibold"
            >
              ← Voltar
            </button>
            <div className="text-xs text-slate-500">
              {step <= domainCount && currentDomain
                ? `Domínio ${step}/${domainCount} — ${
                    Object.keys(answers[String(currentDomain.id)] || {}).length
                  }/${currentDomain.questions.length} respondidas`
                : step === LEGACY_STEP ? (isIGA ? 'Contexto técnico' : 'Contexto da operação')
                : step === CONTACT_STEP ? 'Seus dados' : ''}
            </div>
            {step < CONTACT_STEP ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-6 py-2.5 rounded-full bg-gradient-to-r from-cyan-600 to-emerald-600 text-white font-bold text-sm shadow-lg hover:shadow-xl hover:scale-[1.02] transition"
              >
                Continuar →
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  const { errors, firstFieldId } = validateCurrentStep()
                  setValidationErrors(errors)
                  if (Object.keys(errors).length > 0) {
                    if (firstFieldId) focusField(firstFieldId)
                    return
                  }
                  handleSubmit()
                }}
                disabled={!canSubmit() || submitting}
                className="px-6 py-2.5 rounded-full bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold text-sm shadow-lg hover:shadow-xl hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 transition"
              >
                {submitting ? 'Enviando…' : 'Concluir & Ver resultado'}
              </button>
            )}
          </nav>
        )}

        {submitError && step === CONTACT_STEP && (
          <div className="mt-4 p-3 rounded-lg bg-red-900/30 border border-red-700/50 text-red-300 text-sm">
            {submitError}
          </div>
        )}
        </>
        )}
      </main>

      <footer className="mt-12 py-6 border-t border-slate-800 text-center text-[11px] text-slate-500">
        Material confidencial · NAI · Avaliação anônima até a etapa final ·
        suporte@nai-it.com
      </footer>
    </div>
    </GlossaryProvider>
  )
}

// ─── Step components ─────────────────────────────────────────────────
function StepIntro({ defn, onStart }: { defn: AssessmentDefinition; onStart: () => void }) {
  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="text-center pt-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/40 text-cyan-400 text-[11px] font-bold uppercase tracking-widest">
          ✱ Nai Compliance Center
        </div>
        <h1 className="mt-6 text-3xl sm:text-5xl font-black tracking-tight leading-tight">
          {defn.name}
        </h1>
        <p className="mt-4 text-lg text-slate-400 max-w-2xl mx-auto">
          {defn.headline}
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <FeatureCard num="10" label="Domínios" desc="Estratégia · Inventário · Ciclo de Vida · Acessos · Certificação · PAM · Terceiros · Monitoramento · Compliance · Cultura" />
        <FeatureCard num="100" label="Perguntas" desc="Escala Likert 0-5 · sistema próximo ao CMMI · pontuação até 500" />
        <FeatureCard num={`~${defn.estimated_minutes} min`} label="Duração" desc="Resultado imediato com recomendações priorizadas" />
      </div>

      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 sm:p-8">
        <h3 className="text-sm font-bold uppercase tracking-widest text-cyan-400 mb-3">O que você recebe</h3>
        <ul className="space-y-2 text-sm text-slate-300">
          <li>→ Score consolidado 0-500 com banda de maturidade (RISCO CRÍTICO até OTIMIZADO)</li>
          <li>→ Pontuação por domínio com gráfico radar comparativo</li>
          <li>→ Recomendações priorizadas baseadas nos seus gaps</li>
          <li>→ Estimativa de complexidade de projeto</li>
          <li>→ Diagnóstico preliminar de seus sistemas e bases autoritativas</li>
        </ul>
      </div>

      <div className="text-center pt-2">
        <button
          type="button"
          onClick={onStart}
          className="px-8 py-4 rounded-full bg-gradient-to-r from-cyan-600 to-emerald-600 text-white font-bold text-base shadow-2xl hover:shadow-cyan-500/30 hover:scale-105 transition"
        >
          Começar diagnóstico →
        </button>
        <p className="mt-3 text-xs text-slate-500">
          Sem login · sem cadastro até a etapa final · dados criptografados em trânsito
        </p>
      </div>
    </div>
  )
}

function FeatureCard({ num, label, desc }: { num: string; label: string; desc: string }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
      <div className="text-3xl font-black text-cyan-400 tracking-tight">{num}</div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-300 mt-1">{label}</div>
      <div className="text-[12px] text-slate-500 mt-2 leading-relaxed">{desc}</div>
    </div>
  )
}

function StepDomain({ domain, scale, answers, onAnswer, errors }: {
  domain: DomainDef; scale: ScaleItem[]; answers: Record<string, number>; onAnswer: (qi: number, v: number) => void
  errors: Record<string, string>
}) {
  return (
    <div className="animate-fadeIn space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: domain.color }}>
          Domínio {domain.id}/10
        </div>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ color: '#fff' }}>
          {domain.name}
        </h2>
        <p className="mt-2 text-sm text-slate-400">{domain.tip}</p>
      </div>

      <div className="space-y-4">
        {domain.questions.map((q, i) => {
          const fid = `q-${domain.id}-${i}`
          const hasError = !!errors[fid]
          return (
            <div
              key={i}
              data-field-id={fid}
              className={`bg-slate-900/60 border rounded-xl p-5 transition ${
                hasError
                  ? 'border-red-500/70 ring-1 ring-red-500/30'
                  : 'border-slate-800 hover:border-slate-700'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="text-xs font-mono font-bold text-slate-500 w-6 flex-shrink-0 mt-0.5">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm sm:text-base text-slate-200 leading-relaxed">{q}</p>
                  <ScaleSelector
                    scale={scale}
                    value={answers[String(i)] ?? null}
                    onChange={(v) => onAnswer(i, v)}
                    color={domain.color}
                  />
                  {hasError && (
                    <p className="mt-2 text-xs text-red-400 font-semibold">{errors[fid]}</p>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function StepLegacy({ sections, values, onChange, errors, stepNumber = 11, isIGA = true }: {
  sections: LegacySectionDef[]; values: Record<string, any>; onChange: (f: string, v: any) => void
  errors: Record<string, string>; stepNumber?: number; isIGA?: boolean
}) {
  const [glossaryOpen, setGlossaryOpen] = useState(false)
  return (
    <div className="animate-fadeIn space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-cyan-400 mb-2">
          Passo {stepNumber} — {isIGA ? 'Contexto técnico' : 'Contexto da operação'}
        </div>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
          {isIGA ? 'Sistemas, bases autoritativas e contexto do projeto' : 'Perfil da cadeia, exposição regulatória e contexto do projeto'}
        </h2>
        <p className="mt-2 text-sm text-slate-400">
          {isIGA
            ? 'Essas informações dimensionam o projeto sem expor sua arquitetura. Nenhum dado técnico sensível precisa ser informado aqui.'
            : 'Essas informações dimensionam o projeto e não entram na nota de maturidade. Nenhum dado sensível precisa ser informado aqui.'}
        </p>
        {isIGA && <div className="mt-3">
          <button
            type="button"
            onClick={() => setGlossaryOpen(v => !v)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-bold hover:bg-cyan-500/20 transition"
          >
            📖 Glossário de termos técnicos {glossaryOpen ? '▾' : '▸'}
          </button>
          {glossaryOpen && (
            <div className="mt-3 bg-slate-900/80 border border-cyan-700/40 rounded-xl p-5 space-y-4">
              {Object.entries(GLOSSARY).map(([key, info]) => (
                <div key={key} className="border-l-2 border-cyan-500/40 pl-3">
                  <div className="text-xs font-bold text-cyan-300">{info.title}</div>
                  <p className="text-[12px] text-slate-300 leading-relaxed mt-1">{info.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>}
      </div>

      {sections.map((sec) => (
        <div key={sec.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-6">
          <h3 className="text-base font-bold text-white mb-1">{sec.title}</h3>
          <p className="text-sm text-slate-400 mb-5">{sec.subtitle}</p>
          <div className="space-y-5">
            {sec.fields.map(f => (
              <LegacyField key={f.id} field={f} value={values[f.id]} values={values} onChange={onChange} error={errors[f.id]} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Glossário de termos técnicos ───────────────────────────────────
const GLOSSARY: Record<string, { title: string; body: string }> = {
  'AS/400': {
    title: 'AS/400 / IBM i / iSeries',
    body: 'Plataforma midrange da IBM (atualmente chamada IBM i) usada em sistemas legados desde os anos 80. Roda aplicações em RPG/CL/COBOL com modelo de segurança proprietário (user profiles, authorities, libraries) — não fala protocolos modernos como SAML/OIDC. Conectar IGA exige conector específico que entenda perfis nativos e revogue acessos via API ou SQL services.',
  },
  'SAML': {
    title: 'SAML — Security Assertion Markup Language',
    body: 'Protocolo XML para autenticação federada e SSO (Single Sign-On). Padrão maduro (versão 2.0 de 2005), amplamente suportado por ERPs, SaaS corporativos e diretórios. Permite que um Identity Provider (IdP) emita "assertions" assinadas que aplicações confiam — sem precisar replicar credenciais.',
  },
  'OIDC': {
    title: 'OIDC — OpenID Connect',
    body: 'Camada de identidade construída sobre o OAuth 2.0. Mais leve e moderno que SAML, usa JSON Web Tokens (JWT). Comum em aplicações cloud-native, mobile e APIs REST. Frequentemente usado junto com SAML — ambos servem para SSO/federação, mas OIDC é mais simples de implementar em apps novos.',
  },
  'SAML/OIDC': {
    title: 'SAML e OIDC — protocolos de autenticação federada',
    body: 'SAML (XML, mais antigo, dominante em ERPs corporativos) e OIDC (JSON/JWT, mais moderno, dominante em cloud/SaaS). Ambos permitem SSO entre múltiplos sistemas usando um IdP central. Aplicações que não suportam nenhum dos dois (legadas, mainframe, RPG) precisam de conector dedicado para entrar na governança de IGA.',
  },
  'CIAM': {
    title: 'CIAM — Customer Identity & Access Management',
    body: 'Diferente do IAM corporativo (focado em empregados), o CIAM gerencia identidades de CLIENTES de produtos digitais. Volumes muito maiores (milhares a milhões), prioriza conversão, federação social (Google/Apple/Facebook), consentimento granular LGPD/GDPR e jornada self-service de cadastro.',
  },
  'PAM': {
    title: 'PAM — Privileged Access Management',
    body: 'Gestão de acessos privilegiados (root, admin, contas de serviço). Usa cofre de credenciais com rotação automática, gravação de sessão, MFA reforçado e Just-in-Time access (zero privilégio permanente). Exemplos: CyberArk, BeyondTrust, Delinea, Senhasegura.',
  },
  'SoD': {
    title: 'SoD — Segregation of Duties / Segregação de Funções',
    body: 'Princípio que separa responsabilidades incompatíveis (ex: quem lança não aprova, quem aprova não paga). Em IGA, traduz-se em regras automáticas que bloqueiam concessão de combinações de acesso conflitantes — preventivo, antes que o usuário consiga executar.',
  },
  'RBAC': {
    title: 'RBAC — Role-Based Access Control',
    body: 'Modelo de autorização que agrupa permissões em papéis (roles) e atribui usuários a papéis. Mais simples de manter que ACLs por usuário. Pode coexistir com ABAC (Attribute-Based) — RBAC + ABAC = modelo híbrido moderno.',
  },
  'JIT': {
    title: 'JIT — Just-in-Time Access',
    body: 'Modelo onde o usuário não tem privilégio permanente. Toda elevação para admin/root passa por aprovação, dura limitado e gera trilha. Combinado com PAM, reduz a janela de exposição de credenciais privilegiadas em >80%.',
  },
}

const GLOSSARY_TERMS = Object.keys(GLOSSARY).sort((a, b) => b.length - a.length)

/** Renders a label and injects (?) tooltip buttons next to glossary terms. */
function renderLabelWithGlossary(label: string) {
  // Build regex matching any glossary term (escape /)
  const escaped = GLOSSARY_TERMS.map(t => t.replace(/[/\\]/g, '\\$&')).join('|')
  const regex = new RegExp(`(${escaped})`, 'g')
  const parts = label.split(regex)
  return parts.map((part, i) => {
    if (GLOSSARY[part]) {
      return (
        <span key={i} className="inline-flex items-baseline gap-1">
          <span>{part}</span>
          <GlossaryHint term={part} />
        </span>
      )
    }
    return <span key={i}>{part}</span>
  })
}

/**
 * GlossaryHint para IGA: agora delega ao modal compartilhado via Context.
 * Mantém compat com chamadas existentes (renderLabelWithGlossary).
 */
function GlossaryHint({ term }: { term: string }) {
  const { open } = useGlossary()
  const info = GLOSSARY[term]
  if (!info) return null
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); open(term) }}
      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-cyan-500/20 text-cyan-300 text-[10px] font-bold border border-cyan-500/40 hover:bg-cyan-500/40 transition cursor-help align-middle"
      aria-label={`Definição de ${term}`}
      title={info.title}
    >?</button>
  )
}

function LegacyField({ field, value, values, onChange, error }: {
  field: LegacyFieldDef; value: any; values: Record<string, any>; onChange: (f: string, v: any) => void
  error?: string
}) {
  // Conditional show
  if (field.depends_on) {
    const dep = values[field.depends_on]
    if (!dep) return null
  }

  const labelEl = (
    <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2">
      {renderLabelWithGlossary(field.label)}
    </label>
  )

  const errorEl = error ? (
    <p className="mt-2 text-xs text-red-400 font-semibold">{error}</p>
  ) : null
  const wrapperClass = error
    ? 'p-3 -m-3 rounded-lg border border-red-500/70 ring-1 ring-red-500/30'
    : ''

  // Detect if "Outro" option is selected (case-insensitive)
  const isOtherToken = (s: string) => /^outro$/i.test((s || '').trim())
  const otherKey = `${field.id}_other`
  const otherValue: string = values[otherKey] || ''

  // Render inline (NOT as a nested component) so React reuses the same DOM node
  // across re-renders, avoiding focus-stealing remounts.
  const otherTextField = (
    <div className="mt-3 pl-3 border-l-2 border-cyan-500/50" key={`${field.id}-other-field`}>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-cyan-300 mb-1.5">
        Descreva o "Outro"
      </label>
      <input
        type="text"
        value={otherValue}
        placeholder="Especifique aqui…"
        onChange={e => onChange(otherKey, e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-cyan-700/60 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm placeholder-slate-500"
      />
    </div>
  )

  if (field.type === 'single_select') {
    const isOtherSelected = isOtherToken(value || '')
    return (
      <div data-field-id={field.id} className={wrapperClass}>
        {labelEl}
        <select
          value={value || ''}
          onChange={e => {
            const v = e.target.value
            onChange(field.id, v)
            // Clear the "_other" description if user changes away from Outro
            if (!isOtherToken(v) && values[otherKey]) onChange(otherKey, '')
          }}
          className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
        >
          <option value="">Selecione…</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {isOtherSelected && otherTextField}
        {errorEl}
      </div>
    )
  }

  if (field.type === 'multi_select') {
    const sel: string[] = Array.isArray(value) ? value : []
    const isOtherSelected = sel.some(isOtherToken)
    return (
      <div data-field-id={field.id} className={wrapperClass}>
        {labelEl}
        <div className="flex flex-wrap gap-2">
          {(field.options || []).map(o => {
            const active = sel.includes(o)
            return (
              <button
                key={o}
                type="button"
                onClick={() => {
                  const next = active ? sel.filter(x => x !== o) : [...sel, o]
                  onChange(field.id, next)
                  // Clear other description if user unselected "Outro"
                  if (active && isOtherToken(o) && values[otherKey]) onChange(otherKey, '')
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  active
                    ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200'
                    : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
                }`}
              >
                {o}
              </button>
            )
          })}
        </div>
        {isOtherSelected && otherTextField}
        {errorEl}
      </div>
    )
  }

  if (field.type === 'boolean') {
    return (
      <div data-field-id={field.id} className={wrapperClass}>
        {labelEl}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onChange(field.id, true)}
            className={`px-5 py-2 rounded-lg text-sm font-bold border transition ${
              value === true ? 'bg-emerald-500/20 border-emerald-400 text-emerald-200' : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-500'
            }`}
          >Sim</button>
          <button
            type="button"
            onClick={() => onChange(field.id, false)}
            className={`px-5 py-2 rounded-lg text-sm font-bold border transition ${
              value === false ? 'bg-slate-700/60 border-slate-500 text-slate-200' : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-500'
            }`}
          >Não</button>
        </div>
        {errorEl}
      </div>
    )
  }

  if (field.type === 'number') {
    return (
      <div data-field-id={field.id} className={wrapperClass}>
        {labelEl}
        <input
          type="number" min={0}
          value={value ?? ''}
          placeholder={field.placeholder}
          onChange={e => onChange(field.id, e.target.value ? parseInt(e.target.value, 10) : null)}
          className="w-full max-w-[220px] px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
        />
        {errorEl}
      </div>
    )
  }

  if (field.type === 'tags') {
    const sel: string[] = Array.isArray(value) ? value : []
    return (
      <div data-field-id={field.id} className={wrapperClass}>
        {labelEl}
        <TagsField
          field={field}
          sel={sel}
          onChange={v => onChange(field.id, v)}
        />
        {errorEl}
      </div>
    )
  }

  return null
}

/** Tags input with suggestion chips + free-form "add custom" support. */
function TagsField({ field, sel, onChange }: {
  field: LegacyFieldDef
  sel: string[]
  onChange: (next: string[]) => void
}) {
  const [customInput, setCustomInput] = useState('')
  const suggestions = field.suggestions || []
  const customs = sel.filter(s => !suggestions.includes(s))

  function addCustom() {
    const v = customInput.trim()
    if (!v) return
    // Case-insensitive dedup
    const exists = sel.some(s => s.toLowerCase() === v.toLowerCase())
    if (exists) {
      setCustomInput('')
      return
    }
    onChange([...sel, v])
    setCustomInput('')
  }

  return (
    <>
      {/* Suggestion chips (toggleable) */}
      <div className="flex flex-wrap gap-2 mb-3">
        {suggestions.map(o => {
          const active = sel.includes(o)
          return (
            <button
              key={o}
              type="button"
              onClick={() => onChange(active ? sel.filter(x => x !== o) : [...sel, o])}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${
                active
                  ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200'
                  : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
              }`}
            >
              {active && '✓ '}{o}
            </button>
          )
        })}
      </div>

      {/* Custom items (added by user) — distinct visual + × remove */}
      {customs.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-slate-800">
          {customs.map(c => (
            <span
              key={c}
              className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-xs font-semibold border bg-violet-500/15 border-violet-400 text-violet-200"
              title="Adicionado manualmente"
            >
              <span className="text-violet-400">✦</span>
              {c}
              <button
                type="button"
                onClick={() => onChange(sel.filter(x => x !== c))}
                className="ml-1 w-5 h-5 inline-flex items-center justify-center rounded-full bg-violet-500/30 hover:bg-violet-500/50 text-violet-100 text-xs font-bold transition"
                aria-label={`Remover ${c}`}
              >×</button>
            </span>
          ))}
        </div>
      )}

      {/* Free-form input: add custom one-by-one */}
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-violet-300 mb-1.5">
          Outro · Adicione um por um
        </label>
        <div className="flex gap-2 max-w-md">
          <input
            type="text"
            value={customInput}
            onChange={e => setCustomInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); addCustom() }
            }}
            placeholder="Digite o nome do sistema e Enter…"
            className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm placeholder-slate-500"
          />
          <button
            type="button"
            onClick={addCustom}
            disabled={!customInput.trim()}
            className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-xs transition whitespace-nowrap"
          >
            + Adicionar
          </button>
        </div>
        <p className="text-[10px] text-slate-500 mt-1.5">
          Pressione Enter ou clique em + para incluir. Pode adicionar quantos quiser.
        </p>
      </div>

      {sel.length > 0 && (
        <p className="text-[11px] text-slate-500 mt-3">
          <strong className="text-slate-300">{sel.length}</strong>{' '}
          {sel.length === 1 ? 'sistema selecionado' : 'sistemas selecionados'}
          {customs.length > 0 && (
            <span className="text-violet-400">
              {' '}· {customs.length} adicionado{customs.length > 1 ? 's' : ''} manualmente
            </span>
          )}
        </p>
      )}
    </>
  )
}

function StepContact({ contact, company, onContactChange, onCompanyChange, onCnpjLookup, cnpjLoading, error, errors }: {
  contact: { full_name: string; email: string; phone: string; job_title: string }
  company: { name: string; cnpj: string; size: string; sector: string }
  onContactChange: (k: string, v: string) => void
  onCompanyChange: (k: string, v: string) => void
  onCnpjLookup: () => void
  cnpjLoading: boolean
  error: string | null
  errors: Record<string, string>
}) {
  return (
    <div className="animate-fadeIn space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-emerald-400 mb-2">
          Última etapa — Seus dados
        </div>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
          Para enviarmos seu relatório
        </h2>
        <p className="mt-2 text-sm text-slate-400">
          Usaremos essas informações apenas para gerar e enviar seu relatório de maturidade.
          Não compartilhamos com terceiros.
        </p>
      </div>

      <section className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 space-y-4">
        <h3 className="text-sm font-bold text-white">Empresa</h3>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2">
            CNPJ (consulta automática)
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={company.cnpj}
              placeholder="00.000.000/0000-00"
              onChange={e => onCompanyChange('cnpj', e.target.value)}
              className="flex-1 px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
            />
            <button
              type="button"
              onClick={onCnpjLookup}
              disabled={cnpjLoading || !company.cnpj}
              className="px-4 py-2.5 rounded-lg bg-cyan-600 text-white font-bold text-xs hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {cnpjLoading ? 'Consultando…' : 'Consultar'}
            </button>
          </div>
          <p className="text-[10px] text-slate-500 mt-1">Opcional — preenche razão social automaticamente</p>
        </div>

        <Input label="Razão Social *" required value={company.name} onChange={v => onCompanyChange('name', v)}
          fieldId="company.name" error={errors['company.name']} />

        <div className="grid sm:grid-cols-2 gap-4">
          <Select label="Porte (colaboradores)" value={company.size} onChange={v => onCompanyChange('size', v)}
            options={['Até 100', '101-1.000', '1.001-3.000', '3.001-5.000', 'Mais de 5.000']} />
          <Select label="Setor" value={company.sector} onChange={v => onCompanyChange('sector', v)}
            options={['Financeiro / Banking', 'Energia / Utilities', 'Agronegócio',
              'Indústria', 'Tecnologia', 'Saúde', 'Varejo', 'Telecom', 'Governo', 'Outro']} />
        </div>
      </section>

      <section className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 space-y-4">
        <h3 className="text-sm font-bold text-white">Você</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Nome completo *" required value={contact.full_name} onChange={v => onContactChange('full_name', v)}
            fieldId="contact.full_name" error={errors['contact.full_name']} />
          <Input label="Cargo" value={contact.job_title} onChange={v => onContactChange('job_title', v)} />
        </div>
        <Input label="E-mail corporativo *" type="email" required value={contact.email} onChange={v => onContactChange('email', v)}
          fieldId="contact.email" error={errors['contact.email']} />
        <Input label="WhatsApp / Telefone *" required value={contact.phone} onChange={v => onContactChange('phone', v)}
          placeholder="+55 11 99999-9999"
          fieldId="contact.phone" error={errors['contact.phone']} />
      </section>

      {error && (
        <div className="p-3 rounded-lg bg-red-900/30 border border-red-700/50 text-red-300 text-sm">
          {error}
        </div>
      )}
    </div>
  )
}

function Input({ label, value, onChange, required, type = 'text', placeholder, fieldId, error }: {
  label: string; value: string; onChange: (v: string) => void; required?: boolean
  type?: string; placeholder?: string
  fieldId?: string; error?: string
}) {
  return (
    <div data-field-id={fieldId}>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2">
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className={`w-full px-3 py-2.5 rounded-lg bg-slate-800 border text-slate-100 focus:outline-none focus:ring-2 text-sm ${
          error
            ? 'border-red-500/70 focus:ring-red-500'
            : 'border-slate-700 focus:ring-cyan-500'
        }`}
      />
      {error && <p className="mt-1.5 text-xs text-red-400 font-semibold">{error}</p>}
    </div>
  )
}

function Select({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void; options: string[]
}) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2">{label}</label>
      <select
        value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
      >
        <option value="">Selecione…</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )
}

function CenteredMessage({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="min-h-screen bg-[#050912] flex items-center justify-center text-center px-4">
      <div>
        <div className="text-cyan-400 text-sm font-bold uppercase tracking-widest mb-3">Nai Compliance Center</div>
        <h1 className="text-2xl font-black text-white">{title}</h1>
        {detail && <p className="text-sm text-slate-400 mt-2 max-w-md">{detail}</p>}
      </div>
    </div>
  )
}
